import { useMutation } from "@tanstack/react-query";
import { api } from "@/common/api/instance";
import {
  CreateLeaseDto,
  UpdateLeaseDto,
  LeasesControllerCreateData,
  LeasesControllerFindAllData,
  LeasesControllerFindOneData,
  LeasesControllerUpdateData,
  LeasesControllerRemoveData,
  LeasesControllerUpdateStatusData,
} from "@/common/api/datasources/Api";

export type LeaseStatus = "ACTIVE" | "COMPLETED" | "CANCELED" | "PENDING";

interface PaginationParams {
  page?: number;
  size?: number;
  status?: LeaseStatus;
  propertyUUID?: string;
  tenantUUID?: string;
  managerUUID?: string;
  ownerUUID?: string;
}

const leasesService = {
  createLease: async (
    data: CreateLeaseDto,
  ): Promise<LeasesControllerCreateData> => {
    const response = await api.leases.leasesControllerCreate(data);
    return response.data;
  },

  getAllLeases: async (
    params: PaginationParams,
  ): Promise<LeasesControllerFindAllData> => {
    const response = await api.leases.leasesControllerFindAll(params);
    return response.data;
  },

  getLeaseByUuid: async (
    uuid: string,
  ): Promise<LeasesControllerFindOneData> => {
    const response = await api.leases.leasesControllerFindOne(uuid);
    return response.data;
  },

  updateLease: async (
    uuid: string,
    data: UpdateLeaseDto,
  ): Promise<LeasesControllerUpdateData> => {
    const response = await api.leases.leasesControllerUpdate(uuid, data);
    return response.data;
  },

  deleteLease: async (uuid: string): Promise<LeasesControllerRemoveData> => {
    const response = await api.leases.leasesControllerRemove(uuid);
    return response.data;
  },

  updateStatus: async (
    uuid: string,
    status: LeaseStatus,
  ): Promise<LeasesControllerUpdateStatusData> => {
    const response = await api.leases.leasesControllerUpdateStatus(
      uuid,
      status,
    );
    return response.data;
  },
};

export const useLeasesQuery = () => {
  const allLeasesQuery = (params: PaginationParams) => ({
    queryKey: ["leases", params],
    queryFn: () => leasesService.getAllLeases(params),
  });

  const leaseByIdQuery = (uuid: string) => ({
    queryKey: ["leases", uuid],
    queryFn: () => leasesService.getLeaseByUuid(uuid),
    enabled: !!uuid,
  });

  const createLease = useMutation({
    mutationFn: (data: CreateLeaseDto) => leasesService.createLease(data),
  });

  const updateLease = useMutation({
    mutationFn: ({ uuid, data }: { uuid: string; data: UpdateLeaseDto }) =>
      leasesService.updateLease(uuid, data),
  });

  const deleteLease = useMutation({
    mutationFn: (uuid: string) => leasesService.deleteLease(uuid),
  });

  const updateStatus = useMutation({
    mutationFn: ({ uuid, status }: { uuid: string; status: LeaseStatus }) =>
      leasesService.updateStatus(uuid, status),
  });

  return {
    allLeasesQuery,
    leaseByIdQuery,
    createLease,
    updateLease,
    deleteLease,
    updateStatus,
  };
};

export type { PaginationParams };

export { leasesService };
